import axios from "axios";

const SSO_BASE_URL = "https://sso.forgedrift.com/api";
const AUTH_STORAGE_KEYS = {
  user: "publicUser",
  userId: "userId",
  token: "publicUserToken",
  accessToken: "access_token",
  session: "publicUserSession",
};
export const AUTH_SESSION_CHANGED_EVENT = "publicUserSessionChanged";

const authClient = axios.create({
  baseURL: SSO_BASE_URL,
  headers: {
    Accept: "application/json, text/plain, */*",
    "Content-Type": "application/json",
  },
});

export const registerPublicUser = (payload) =>
  authClient.post("/registerPublicUser", {
    email: payload.email,
    password: payload.password,
    firstName: payload.firstName,
    lastName: payload.lastName,
    phoneNumber: payload.phoneNumber,
  });

export const authenticatePublicUser = (payload) =>
  authClient.post("/authenticatePublicUser", {
    email: payload.email,
    password: payload.password,
  });

const getAuthResponse = (data) => data?.response || data?.data || data;

const getAccessToken = (authResponse) => {
  return (
    authResponse?.authentication?.accessToken ||
    authResponse?.token ||
    authResponse?.accessToken ||
    authResponse?.access_token ||
    authResponse?.jwt ||
    authResponse?.idToken
  );
};

const buildUserInfo = (authResponse) => {
  if (!authResponse) return null;

  const profile = authResponse.profile || {};

  return {
    id: authResponse._id || authResponse.id || "",
    email: authResponse.email || "",
    firstName: profile.firstName || authResponse.firstName || "",
    lastName: profile.lastName || authResponse.lastName || "",
    phoneNumber: profile.phoneNumber || authResponse.phoneNumber || "",
    emailVerified: Boolean(authResponse.verify?.emailVerified),
    enabled:
      typeof authResponse.enabled === "boolean" ? authResponse.enabled : true,
  };
};

const buildSessionInfo = (authResponse, token) => {
  return {
    accessToken: token,
    expiry: authResponse?.authentication?.expiry || null,
    createdAt: new Date().toISOString(),
  };
};

export const persistAuthSession = (data) => {
  const authResponse = getAuthResponse(data);
  if (!authResponse) return null;

  const token = getAccessToken(authResponse);
  const userInfo = buildUserInfo(authResponse);

  if (userInfo) {
    localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(userInfo));
    if (userInfo.id) {
      localStorage.setItem(AUTH_STORAGE_KEYS.userId, userInfo.id);
    }
  }

  if (token) {
    localStorage.setItem(AUTH_STORAGE_KEYS.token, token);
    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, token);
    localStorage.setItem(
      AUTH_STORAGE_KEYS.session,
      JSON.stringify(buildSessionInfo(authResponse, token))
    );
  }

  window.dispatchEvent(
    new CustomEvent(AUTH_SESSION_CHANGED_EVENT, { detail: userInfo })
  );

  return userInfo;
};

export const getStoredPublicUser = () => {
  try {
    const storedUser = localStorage.getItem(AUTH_STORAGE_KEYS.user);
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    console.error("getStoredPublicUser error:", error);
    return null;
  }
};

export const getStoredPublicUserToken = () => {
  return (
    localStorage.getItem(AUTH_STORAGE_KEYS.token) ||
    localStorage.getItem(AUTH_STORAGE_KEYS.accessToken) ||
    ""
  );
};

export const getStoredPublicUserSession = () => {
  try {
    const storedSession = localStorage.getItem(AUTH_STORAGE_KEYS.session);
    return storedSession ? JSON.parse(storedSession) : null;
  } catch (error) {
    console.error("getStoredPublicUserSession error:", error);
    return null;
  }
};

export const clearAuthSession = () => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.user);
  localStorage.removeItem(AUTH_STORAGE_KEYS.userId);
  localStorage.removeItem(AUTH_STORAGE_KEYS.token);
  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.session);
  localStorage.removeItem("accessToken");

  window.dispatchEvent(
    new CustomEvent(AUTH_SESSION_CHANGED_EVENT, { detail: null })
  );
};

export const getPublicUserName = (user) => {
  if (!user) return "";

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return fullName || user.email || "";
};
